import React, { useState, useContext, useEffect } from 'react';

import { Modal, Box, Typography, Stack, TextField, FormHelperText, Divider, Switch, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { DesktopTimePicker } from '@mui/x-date-pickers/DesktopTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useForm, Controller, useFieldArray } from "react-hook-form";
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from "yup";
import { format, isValid, parse } from 'date-fns';
import { doc, updateDoc, setDoc } from 'firebase/firestore';

import { db } from '../../../../firebase';
import AlertsContext from '../../../../AlertsContext';
import slugify from 'slugify';
import { getAustraliaTimeZones } from '../../../../helpers';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 400,
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 4,
  maxHeight: "80%",
  overflow: "scroll",
  scroll: "auto",
};

const HourSchema = yup.object().shape({
  day: yup.string(),
  enabled: yup.boolean(),
  from: yup.date().transform(value => {
    return value ? new Date(value) : null;
  }).nullable().when('enabled', {
    is: true,
    then: (schema) => schema.required().label("From"),
  }),
  to: yup.date().transform(value => {
    return value ? new Date(value) : null;
  }).nullable().when('enabled', {
    is: true,
    then: (schema) => schema.required().label("To").when('from', ([from], schema) => {
      if (!!from && isValid(from) && schema) {
        return schema
          .min(from, 'To Date must be after From Date')
      }
    }),
  }),
})

const LocationSchema = yup.object({
  name: yup.string().required().label('Name'),
  timeZone: yup.string().required().label("Time Zone"),
  hours: yup.array().of(HourSchema),
}).required();

const days = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];

export default function LocationModal({ currentLocation, open = false, onClose }) {
  const { setAlert } = useContext(AlertsContext);
  const [isLoading, setIsLoading] = useState(false);
  const { setValue, getValues, control, handleSubmit, reset, formState: { errors, isDirty, isValid } } = useForm({
    defaultValues: {
      name: '',
      timeZone: '',
      address: '',
      hours: days.map((day) => ({
        day,
        enabled: false,
        from: null,
        to: null,
      }))
    },
    resolver: yupResolver(LocationSchema)
  });
  const { fields } = useFieldArray({
    control,
    name: "hours",
  });

  const handleOnClose = () => {
    reset();
    if (onClose) onClose();
  }

  const onSubmit = async data => {
    try {
      setIsLoading(true);
      const hours = data
        ?.hours
        ?.filter((hour) => hour.enabled)
        ?.reduce((hourObj = {}, item) => {
          if (item?.from && item?.to) {
            return {
              ...hourObj,
              [item.day]: {
                from: format(new Date(item.from), 'HH:mm'),
                to: format(new Date(item.to), 'HH:mm'),
              }
            };
          }

          return hourObj;
        }, {}) ?? [];

      const newLocation = {
        ...data,
        hours,
      }

      if (currentLocation) {
        const locationRef = doc(db, "locations", currentLocation.id);
        await updateDoc(locationRef, {
          ...newLocation,
        })
        setAlert("Location is updated successful.");
      } else {
        const id = `${slugify(data?.name?.toLowerCase())}`;
        const locationRef = doc(db, "locations", id);
        await setDoc(locationRef, {
          ...newLocation,
        })

        setAlert("Location is created successful.")
      }

      reset();
      onClose();
    } catch (e) {
      setAlert("Error.")
    } finally {
      setIsLoading(false);
    }
  }

  const renderDays = () => {
    return fields.map((field, index) => {
      return (
        <Stack key={field.id} direction={"column"} spacing={2}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography>{field?.day?.toUpperCase()}</Typography>
            <Controller
              name={`hours.${index}.enabled`}
              control={control}
              render={({ field }) => <Switch {...field} checked={field.value} onChange={(event) => {
                field.onChange(event.target.checked)
              }} />}
            />
          </Stack>
          <Stack direction="row" spacing={4}>
            <Box>
              <Controller
                name={`hours.${index}.from`}
                control={control}
                render={({ field }) => <DesktopTimePicker
                  label="From"
                  inputFormat="HH:mm"
                  value={field?.value}
                  onChange={value => field.onChange(value)}
                  renderInput={(params) => <TextField {...field} {...params} />}
                />}
              />
              {
                errors?.hours?.[index]?.from && (
                  <FormHelperText error>{errors?.hours?.[index]?.from?.message}</FormHelperText>
                )
              }
            </Box>
            <Box>
              <Controller
                name={`hours.${index}.to`}
                control={control}
                render={({ field }) => <DesktopTimePicker
                  label="To"
                  inputFormat="HH:mm"
                  value={field.value}
                  onChange={value => field.onChange(value)}
                  renderInput={(params) => <TextField {...field} {...params} />}
                />}
              />
              {
                errors?.hours?.[index]?.to && (
                  <FormHelperText error>{errors?.hours?.[index]?.to?.message}</FormHelperText>
                )
              }
            </Box>
          </Stack>
        </Stack>
      )
    })
  }

  useEffect(() => {
    if (currentLocation) {
      setValue("name", currentLocation.name);
      setValue("timeZone", currentLocation?.timeZone);
      setValue("address", currentLocation?.address);
      const hours = getValues("hours");
      setValue("hours", hours.map((hour) => {
        const currentLocationHour = currentLocation?.hours?.[hour.day];
        if (currentLocationHour) {
          return {
            ...hour,
            enabled: true,
            from: parse(currentLocationHour?.from, "HH:mm", new Date()),
            to: parse(currentLocationHour?.to, "HH:mm", new Date()),
          }
        }

        return hour;
      }))
    }
  }, [currentLocation])

  return <>
    <LocalizationProvider dateAdapter={AdapterDateFns} >
      <Modal
        open={open}
        onClose={() => handleOnClose()}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={style}>
          <Typography id="modal-modal-title" variant="h6" component="h2" sx={{
            mb: 4,
          }}>
            {!currentLocation ? 'Add' : 'Edit'} Location
          </Typography>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Stack spacing={2}>
              <Controller
                name="name"
                control={control}
                render={({ field }) => <TextField type="text" id="outlined-basic" label="Location Name" variant="outlined" {...field} />}
              />
              {
                errors?.name && (
                  <FormHelperText id="my-helper-text" error>{errors.name?.message}</FormHelperText>
                )
              }
              <FormControl style={{ width: "100%" }}>
                <InputLabel>Time Zone</InputLabel>
                <Controller
                  name="timeZone"
                  control={control}
                  render={({ field }) => <Select
                    value={field.value}
                    label="Time Zone"
                    onChange={(event) => {
                      field.onChange(event.target.value)
                    }}
                    style={{ minWidth: "100%" }}
                  >
                    {
                      getAustraliaTimeZones().map((timeZone) => <MenuItem key={timeZone} value={timeZone}>{timeZone}</MenuItem>)
                    }
                  </Select>}
                />
              </FormControl>
              <FormControl style={{ width: "100%" }}>
                <Controller
                  name="address"
                  control={control}
                  render={({ field }) => <TextField type='textarea' minRows={3} placeholder='Address' multiline {...field} />}
                />
              </FormControl>
              <Divider textAlign="left">Hours</Divider>
              {renderDays()}
              <LoadingButton
                disabled={!isDirty && !isValid}
                loading={isLoading}
                sx={{ margin: '0 auto', marginTop: 2, alignItems: 'center' }}
                type='submit'
                variant='contained'
              >
                Add
              </LoadingButton>
            </Stack>
          </form>
        </Box>
      </Modal>
    </LocalizationProvider>
  </>
}
